import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LangTabs, useLangForm } from '../../components/ui/lang-tabs';
import { toast } from 'sonner';
import { BadgeJapaneseYen, Save } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPricingPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', last_revised: '' });
  const { lang, setLang, getField, setField } = useLangForm(form, ['title', 'content']);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/settings/pricing`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      const { key, updated_at, updated_by, ...data } = res.data;
      setForm(prev => ({ ...prev, ...data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings/pricing`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
      toast.success(t('pricing_saved'));
    } catch (error) {
      toast.error(t('content_save_failed'));
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="text-center py-12 text-muted-foreground">{t('loading')}</div></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-pricing-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
              <BadgeJapaneseYen className="h-8 w-8" />
              {t('pricing_admin_title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('pricing_admin_subtitle')}</p>
          </div>
          <LangTabs activeLang={lang} onChange={setLang} />
        </div>
        <div className="bg-card border border-border/50 rounded-2xl p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>{t('legal_title_label')}{lang !== 'ja' && <span className="text-xs text-muted-foreground ml-2">({lang.toUpperCase()})</span>}</Label>
              <Input value={getField(form, 'title')} onChange={(e) => setField(form, setForm, 'title', e.target.value)} placeholder={t('pricing_title_placeholder')} data-testid="pricing-title-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('legal_revised_label')}</Label>
              <Input value={form.last_revised} onChange={(e) => setForm({ ...form, last_revised: e.target.value })} placeholder="2026年1月1日" data-testid="pricing-revised-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('legal_content_label')}{lang !== 'ja' && <span className="text-xs text-muted-foreground ml-2">({lang.toUpperCase()})</span>}</Label>
            <textarea value={getField(form, 'content')} onChange={(e) => setField(form, setForm, 'content', e.target.value)} rows={18} className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 font-mono leading-relaxed" placeholder={t('pricing_content_placeholder')} data-testid="pricing-content-input" />
            <p className="text-xs text-muted-foreground">{t('legal_content_hint')}</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-pricing-btn">
            <Save className="h-4 w-4" />{saving ? t('admin_settings_saving') : t('pricing_save_btn')}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
